import type { LinePaidCost } from "@loot-os/fulfillment-core/order/stockx-size-refund";
import type { OrderStatus, OrderType } from "@loot-os/fulfillment-core/order/types";
import { Button } from "@loot-os/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@loot-os/ui/components/card";
import { Input } from "@loot-os/ui/components/input";
import { Label } from "@loot-os/ui/components/label";
import { toast } from "@loot-os/ui/components/sonner";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { transitionTargets } from "../lib/orderActions";
import { buildPaidCosts } from "../lib/paidCosts";
import { useTRPC } from "../lib/trpc";

/** A StockX line the move to PROCESSING asks a paid cost for, with what the fulfiller reads it by. */
export interface StockxPaidCostLine {
  id: string;
  label: string;
}

/**
 * Walks an order along the state machine and carries the tracking number with
 * the move, which is the only way the dashboard writes one. `fromStatus` makes
 * the write conflict rather than skip a step when someone else moved it first.
 * The move to PROCESSING also carries what Loot OS paid StockX for each StockX line,
 * which the size refund is computed from (ENG-7343).
 */
export function OrderTransitionActions({
  order,
  stockxLines,
}: {
  order: { id: string; status: OrderStatus; type: OrderType; trackingNumber: string | null };
  stockxLines: StockxPaidCostLine[];
}) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [trackingNumber, setTrackingNumber] = useState(order.trackingNumber ?? "");
  const [paidDollarsByLineId, setPaidDollarsByLineId] = useState<Record<string, string>>({});

  const transition = useMutation(
    trpc.platform.fulfillment.orders.transition.mutationOptions({
      onSuccess: (updated) => {
        toast.success(`Order is ${updated.status}.`);
        void queryClient.invalidateQueries({
          queryKey: trpc.platform.fulfillment.orders.pathKey(),
        });
      },
    }),
  );

  const targets = transitionTargets(order.status, order.type);
  if (targets.length === 0) return null;
  const asksPaidCosts = targets.includes("PROCESSING") && stockxLines.length > 0;

  /** Submit the move and tracking number; require valid dollar amounts for StockX processing. */
  function move(target: (typeof targets)[number]) {
    let paidCosts: LinePaidCost[] | undefined;
    if (target === "PROCESSING" && stockxLines.length > 0) {
      const entered = buildPaidCosts(
        stockxLines.map((line) => line.id),
        paidDollarsByLineId,
      );
      if (entered === null) {
        toast.error(
          "Enter the StockX checkout total for every StockX line, in dollars, above $0.00.",
        );
        return;
      }
      paidCosts = entered;
    }
    transition.mutate({
      id: order.id,
      toStatus: target,
      fromStatus: order.status,
      trackingNumber: trackingNumber.trim() || undefined,
      paidCosts,
    });
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Move this order on</CardTitle>
        <CardDescription>
          PROCESSING once the buy is placed, PRESHIPPED once the vendor verified the item, SHIPPED
          once it is on its way. The tracking number below is saved with the move.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="grid max-w-sm gap-2">
          <Label htmlFor="tracking-number">Tracking number</Label>
          <Input
            id="tracking-number"
            value={trackingNumber}
            disabled={transition.isPending}
            onChange={(e) => setTrackingNumber(e.target.value)}
          />
        </div>
        {asksPaidCosts ? (
          <div className="grid max-w-sm gap-3">
            <p className="text-sm text-muted-foreground">
              The StockX checkout total for each line: the price of the size actually bought,
              including tax, excluding shipping. The size refund is worked out from it on the move
              to PROCESSING.
            </p>
            {stockxLines.map((line) => (
              <div key={line.id} className="grid gap-2">
                <Label htmlFor={`paid-cost-${line.id}`}>
                  StockX checkout total, {line.label} (USD)
                </Label>
                <Input
                  id={`paid-cost-${line.id}`}
                  inputMode="decimal"
                  placeholder="0.00"
                  value={paidDollarsByLineId[line.id] ?? ""}
                  disabled={transition.isPending}
                  onChange={(e) =>
                    setPaidDollarsByLineId((current) => ({
                      ...current,
                      [line.id]: e.target.value,
                    }))
                  }
                />
              </div>
            ))}
          </div>
        ) : null}
        <div className="flex flex-wrap gap-2">
          {targets.map((target) => (
            <Button
              key={target}
              type="button"
              variant="outline"
              disabled={transition.isPending}
              onClick={() => move(target)}
            >
              {target}
            </Button>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
